import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { CustomValidators } from '@nimmsta/ng-commons-lib/utils/custom-validators';
import {
  DataConnection,
  Pageable,
  Workflow,
  WorkflowTemplate,
} from '@nimmsta/ng-commons-lib/models';
import {
  SaveableFormComponent,
  InputWithErrorComponent,
} from '@nimmsta/ng-commons-lib/forms';
import { WorkflowService } from '@nimmsta/ng-commons-lib/services/workflow';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { ContentsService } from '@nimmsta/ng-commons-lib/services/contents';
import { WorkflowTemplateService } from '../services/workflow-template.service';
import { DataConnectionService } from '../services/dataConnection.service';
import { NgClass } from '@angular/common';
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from 'primeng/fileupload';
import { Checkbox } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import {
  DomSanitizer,
  SafeResourceUrl,
  SafeUrl,
} from '@angular/platform-browser';
import { Subscription, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  classifyWorkflowMediaPreview,
  WorkflowMediaPreview,
  WorkflowMediaPreviewType,
} from './workflow-media-preview.util';

_('workflow.default_for_job_type');

@Component({
  selector: 'app-workflow-form',
  templateUrl: 'workflow-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NgClass,
    InputWithErrorComponent,
    ErrorComponent,
    TranslatePipe,
    FileUploadModule,
    ButtonModule,
    Checkbox,
  ],
})
export class WorkflowFormComponent
  extends SaveableFormComponent<Workflow>
  implements OnInit, OnDestroy
{
  private fb = inject(FormBuilder);
  private templateService = inject(WorkflowTemplateService);
  private dataConnectionService = inject(DataConnectionService);
  private translate = inject(TranslateService);
  private contentsService = inject(ContentsService);
  private http = inject(HttpClient);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  templates: WorkflowTemplate[] = [];

  dataConnectionIds: string[] = [];

  dataConnectionNames: string[] = [];

  workflowTemplateIds: string[] = [];

  workflowTemplateNames: string[] = [];

  step = 1;

  @ViewChild(FileUpload) private fileUpload?: FileUpload;

  uploadingFile = false;

  currentFile?: File;

  previewType?: WorkflowMediaPreviewType;

  previewUrl?: SafeUrl;

  previewResourceUrl?: SafeResourceUrl;

  previewSrcDoc?: string;

  protected override dataService = inject(WorkflowService);
  sanitizer = inject(DomSanitizer);

  private existingFileUrl?: string;
  private currentObjectUrl?: string;
  private htmlPreviewObjectUrl?: string;
  private signedUrlSubscription?: Subscription;
  private embedStringSubscription?: Subscription;
  private dashboardReplacementSubscription?: Subscription;

  constructor() {
    super();
    this.setValue({} as Workflow);
  }

  ngOnInit(): void {
    this.dataConnectionService
      .simpleList()
      .subscribe((connections: DataConnection[]) => {
        connections.sort((a, b) => a.name.localeCompare(b.name));
        this.dataConnectionIds = connections.map(
          (dataConnection) => dataConnection.id,
        );
        this.dataConnectionNames = connections.map(
          (dataConnection) => dataConnection.name,
        );
      });
    this.templateService
      .list({
        filter: ['status:ACTIVE-and'],
        sortColumn: 'name',
        pageSize: 9999,
      })
      .subscribe((page: Pageable<WorkflowTemplate>) => {
        this.templates = page.content;
        this.workflowTemplateIds = [
          '',
          ...page.content.map((workflow) => workflow.id),
        ];
        this.workflowTemplateNames = [
          this.translate.instant('workflow.default.customWorkflow'),
          ...page.content.map((workflow) => workflow.name),
        ];
        this.syncDashboardReplacementControl();
      });
  }

  override setValue(value: Workflow): void {
    this.resetPreviewState();
    if (value && value.dataConnection) {
      value.dataConnectionId = value.dataConnection.id;
    }
    if (value && value.workflowTemplate) {
      value.workflowTemplateId = value.workflowTemplate.id;
    }
    super.setValue(value);
    this.step = 1;
    if (this.value.id != null) {
      this.step++;
    }
    this.subscribeToEmbedStringChanges();
    this.form.patchValue(value);
    this.updatePreview();
    if (value.fileKey) {
      this.signedUrlSubscription = this.contentsService
        .getSignedUrl(value.fileKey)
        .subscribe((signedUrl: string) => {
          this.existingFileUrl = signedUrl;
          this.updatePreview();
        });
    }
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({
      id: this.value.id,
      identifier: [
        this.value.identifier,
        [
          Validators.required,
          CustomValidators.patternValidator(/^[a-zA-Z0-9-]*$/, {
            noSpaceOrSpecial: true,
          }),
        ],
      ],
      name: [
        this.value.name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255),
        ],
      ],
      description: [
        this.value.description,
        [Validators.maxLength(255), Validators.minLength(2)],
      ],
      dataConnectionId: [this.value.dataConnectionId],
      workflowTemplateId: [this.value.workflowTemplateId],
      isIdle: [this.value.isIdle],
      fileKey: [this.value.fileKey],
      embedString: [this.value.embedString],
      replaceDashboard: [{ value: false, disabled: true }],
    });
    this.dashboardReplacementSubscription?.unsubscribe();
    this.dashboardReplacementSubscription = this.form.valueChanges.subscribe(
      () => {
        this.syncDashboardReplacementControl();
      },
    );
  }

  onUpload(event: FileUploadHandlerEvent): void {
    if (event.files && event.files.length) {
      const file = event.files[0];
      this.clearCurrentObjectUrl();
      this.currentFile = file;
      this.currentObjectUrl = URL.createObjectURL(file);
      this.updatePreview();
    }
  }

  hasFile(): boolean {
    return !!(this.currentFile || this.form.controls['fileKey'].value);
  }

  removeFile(): void {
    if (!this.editable || this.form.disabled || this.uploadingFile) {
      return;
    }
    this.signedUrlSubscription?.unsubscribe();
    this.currentFile = undefined;
    this.existingFileUrl = undefined;
    this.clearCurrentObjectUrl();
    this.fileUpload?.clear();
    this.form.controls['fileKey'].setValue(null);
    this.form.markAsDirty();
    this.updatePreview();
  }

  override onSave(): void {
    if (this.uploadingFile) {
      return;
    }
    if (this.currentFile) {
      this.uploadingFile = true;
      this.loadingEvent.emit(true);
      this.contentsService
        .getSignedUploadUrl(this.currentFile.name, this.currentFile.type)
        .pipe(
          tap(({ key }) => {
            this.form.controls['fileKey'].setValue(key);
            this.form.markAsDirty();
          }),
          switchMap(({ url }) => this.http.put(url, this.currentFile)),
        )
        .subscribe({
          next: () => {
            this.uploadingFile = false;
            super.onSave();
          },
          error: (error) => {
            this.uploadingFile = false;
            this.loadingEvent.emit(false);
            this.error = error;
          },
        });
    } else {
      super.onSave();
    }
  }

  nextStep(): void {
    if (
      this.isDefaultSelected() &&
      (<string>this.form.controls['workflowTemplateId'].value).length
    ) {
      const workflowTemplate = this.templates.find(
        (workflow) =>
          workflow.id === this.form.controls['workflowTemplateId'].value,
      )!;
      this.value.workflowTemplateId = workflowTemplate.id;
      this.value.definition = workflowTemplate.definition;
      this.value.fileKey = workflowTemplate.fileKey;
      this.value.embedString = workflowTemplate.embedString;
    }
    this.form.patchValue(this.value);
    this.step++;
  }

  isDefaultSelected(): boolean {
    return (
      this.form.controls['workflowTemplateId'].value != null ||
      this.value.workflowTemplate != null
    );
  }

  shouldShowDashboardReplacement(): boolean {
    return (
      !this.value.id &&
      typeof this.form.controls['workflowTemplateId'].value === 'string' &&
      this.form.controls['workflowTemplateId'].value.length > 0
    );
  }

  selectedTemplateHasDashboard(): boolean {
    const templateId = this.form.controls['workflowTemplateId'].value;
    return !!this.templates.find(
      (template) => template.id === templateId && template.hasDashboard,
    );
  }

  canReplaceDashboard(): boolean {
    return (
      this.shouldShowDashboardReplacement() &&
      this.selectedTemplateHasDashboard() &&
      typeof this.form.controls['dataConnectionId'].value === 'string' &&
      this.form.controls['dataConnectionId'].value.length > 0
    );
  }

  private syncDashboardReplacementControl(): void {
    const control = this.form.controls['replaceDashboard'];
    if (this.canReplaceDashboard()) {
      control.enable({ emitEvent: false });
      return;
    }

    control.setValue(false, { emitEvent: false });
    control.disable({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.dashboardReplacementSubscription?.unsubscribe();
    this.resetPreviewState();
  }

  private subscribeToEmbedStringChanges(): void {
    this.embedStringSubscription?.unsubscribe();
    this.embedStringSubscription = this.form.controls[
      'embedString'
    ].valueChanges.subscribe(() => {
      this.updatePreview();
    });
  }

  private updatePreview(): void {
    const preview = classifyWorkflowMediaPreview({
      embedString: <string | null | undefined>(
        this.form.controls['embedString'].value
      ),
      fileUrl: this.currentObjectUrl ?? this.existingFileUrl,
      fileType: this.currentFile?.type,
      fileName: this.currentFile?.name,
      fileKey: <string | null | undefined>this.form.controls['fileKey'].value,
    });
    this.applyPreview(preview);
  }

  private applyPreview(preview: WorkflowMediaPreview | null): void {
    this.clearRenderedPreview();
    if (!preview) {
      return;
    }

    this.previewType = preview.type;
    if (preview.type === 'image' && preview.src) {
      this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(preview.src);
      return;
    }

    if ((preview.type === 'iframe' || preview.type === 'pdf') && preview.src) {
      this.previewResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        preview.src,
      );
      return;
    }

    if (preview.type === 'html' && preview.srcDoc) {
      this.htmlPreviewObjectUrl = URL.createObjectURL(
        new Blob([preview.srcDoc], { type: 'text/html' }),
      );
      this.previewResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.htmlPreviewObjectUrl,
      );
    }
  }

  private resetPreviewState(): void {
    this.signedUrlSubscription?.unsubscribe();
    this.embedStringSubscription?.unsubscribe();
    this.currentFile = undefined;
    this.existingFileUrl = undefined;
    this.clearCurrentObjectUrl();
    this.clearRenderedPreview();
  }

  private clearRenderedPreview(): void {
    this.clearHtmlPreviewObjectUrl();
    this.previewType = undefined;
    this.previewUrl = undefined;
    this.previewResourceUrl = undefined;
    this.previewSrcDoc = undefined;
  }

  private clearCurrentObjectUrl(): void {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = undefined;
    }
  }

  private clearHtmlPreviewObjectUrl(): void {
    if (this.htmlPreviewObjectUrl) {
      URL.revokeObjectURL(this.htmlPreviewObjectUrl);
      this.htmlPreviewObjectUrl = undefined;
    }
  }
}
